import { useEffect } from "react";

export interface ShortcutHandlers {
  onOpenPalette: () => void;
  onNewChat: () => void;
  onToggleTheme: () => void;
  onToggleDebug: () => void;
  onOpenSettings: () => void;
  onFocusComposer: () => void;
  onEscape: () => void;
}

const isEditable = (el: EventTarget | null): boolean => {
  const node = el as HTMLElement | null;
  return !!node && (node.tagName === "INPUT" || node.tagName === "TEXTAREA" || node.isContentEditable);
};

/**
 * Global keyboard shortcuts. Meta/Ctrl based so they don't collide with typing:
 * ⌘K palette · ⌘⇧K new chat · ⌘J theme · ⌘D debug · ⌘, settings · / focus composer · Esc close.
 */
export function useKeyboardShortcuts(handlers: ShortcutHandlers): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const mod = e.metaKey || e.ctrlKey;
      if (e.key === "Escape") return handlers.onEscape();
      if (mod && e.key.toLowerCase() === "k") {
        e.preventDefault();
        return e.shiftKey ? handlers.onNewChat() : handlers.onOpenPalette();
      }
      if (mod && e.key.toLowerCase() === "j") {
        e.preventDefault();
        return handlers.onToggleTheme();
      }
      if (mod && e.key.toLowerCase() === "d") {
        e.preventDefault();
        return handlers.onToggleDebug();
      }
      if (mod && e.key === ",") {
        e.preventDefault();
        return handlers.onOpenSettings();
      }
      if (e.key === "/" && !isEditable(e.target)) {
        e.preventDefault();
        return handlers.onFocusComposer();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handlers]);
}
